import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LinkIp3PhoneFormat } from '@reform-lite/contracts';
import { apiClient } from '@/lib/api-client';

/**
 * RL-570 — integration profile 3 ("CRM loyalty") sources. Back Office stores
 * them and hands them to the Link of their store; it never calls the CRM.
 * The password is write-only: no response carries it, so the form can only
 * replace it.
 */

export type Ip3Operations = {
  cardLookup: boolean;
  phoneLookup: boolean;
  receiptPreference: boolean;
  /** RL-587: send carded receipts to the CRM (it awards bonuses from them). Absent reads as off. */
  receiptSubmission?: boolean;
  /** VL-103: hand failed receipts to the store's offline processing. Absent reads as off. */
  offlineProcessing?: boolean;
};

/** The profile's settings as Back Office stores them. Never the password. */
export type Ip3Settings = {
  baseUrl: string;
  allowInsecureHttp: boolean;
  timeoutMs: number;
  tradePointCode: string;
  phoneFormat: LinkIp3PhoneFormat;
  operations: Ip3Operations;
};

export type Ip3Source = {
  id: string;
  profile: 'ip-3';
  name: string;
  username: string;
  enabled: boolean;
  settings: Ip3Settings;
  updatedAt: string;
  location: { id: string; code: string; name: string };
};

export type Ip3SourceInput = {
  locationId: string;
  name: string;
  username: string;
  password: string;
  enabled: boolean;
  settings: Ip3Settings;
};

/** An edit. `password` absent keeps the stored one; it cannot be read back. */
export type Ip3SourceChange = {
  name?: string;
  username?: string;
  password?: string;
  enabled?: boolean;
  settings?: Partial<Omit<Ip3Settings, 'operations'>> & { operations?: Partial<Ip3Operations> };
  /** The `updatedAt` the form loaded; the server refuses a stale edit (409). */
  expectedUpdatedAt?: string;
  /** With `enabled: true`: the store's enabled source this one takes over from. */
  replaces?: string;
};

const KEY = ['ip3-sources'] as const;
const PATH = '/connectors/ip-3/sources';

export function useIp3Sources(enabled: boolean) {
  return useQuery({
    queryKey: KEY,
    enabled,
    queryFn: async (): Promise<Ip3Source[]> => {
      const response = await apiClient.get<Ip3Source[]>(PATH);
      return response.data;
    },
  });
}

export function useCreateIp3Source() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Ip3SourceInput) => {
      await apiClient.post(PATH, input);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateIp3Source() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sourceId: string; change: Ip3SourceChange }) => {
      await apiClient.patch(`${PATH}/${input.sourceId}`, input.change);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}
